// ============================================
// Safety Profile → ModifierSet Mapper
//
// Pure function: maps NRS.08 SafetyDataProfile
// to the ModifierSet consumed by the protocol
// pipeline (Phase2, pre-filter, gate classifier).
//
// Deduplicates modal + chat sources.
// Maps reproductive status + pregnancy flags.
// ============================================

import type { ModifierSet, Medication } from '../../types/patientJourney';
import { EMPTY_MODIFIER_SET } from '../../types/patientJourney';
import type { SafetyDataProfile } from '../../../functions/src/nurse/types/sessionStateHandoff';

/**
 * Convert SafetyDataProfile (NRS.08 output) to ModifierSet
 * (protocol pipeline input).
 *
 * Deduplication: modal data takes precedence, chat data
 * supplements. Duplicate entries are merged by lowercase match.
 */
export function safetyProfileToModifiers(
  profile: SafetyDataProfile,
  existingModifiers?: Partial<ModifierSet>,
): ModifierSet {
  // Deduplicate strings by lowercase
  function dedup(primary: string[], secondary: string[]): string[] {
    const seen = new Set(primary.map(s => s.toLowerCase()));
    const result = [...primary];
    for (const item of secondary) {
      if (!seen.has(item.toLowerCase())) {
        seen.add(item.toLowerCase());
        result.push(item);
      }
    }
    return result;
  }

  // Merge medications: modal + chat, deduplicated
  const allMedNames = dedup(profile.modalMedications, profile.chatMedications);
  const medications: Medication[] = allMedNames.map(name => ({
    name,
    dose: '',
    category: 'unknown',
    duration: '',
  }));

  // Merge allergies: modal + chat, deduplicated
  const allergies = dedup(profile.modalAllergies, profile.chatAllergies);

  // Merge conditions: modal + chat, deduplicated
  const conditions = dedup(profile.modalConditions, profile.chatConditions);

  // Map reproductive status from profile flags
  let reproductiveStatus: ModifierSet['reproductiveStatus'] = 'not_applicable';
  if (profile.reproductiveStatus) {
    reproductiveStatus = profile.reproductiveStatus;
  } else if (profile.isPregnant) {
    reproductiveStatus = 'pregnant';
  } else if (profile.isBreastfeeding) {
    reproductiveStatus = 'breastfeeding';
  }

  return {
    ...EMPTY_MODIFIER_SET,
    ...existingModifiers,
    allergies,
    medications,
    conditions,
    reproductiveStatus,
    // Carry forward non-profile fields from existing modifiers
    goals: existingModifiers?.goals ?? [],
    lifestyle: existingModifiers?.lifestyle ?? [],
    budgetTier: existingModifiers?.budgetTier ?? 'all_tiers',
    intent: existingModifiers?.intent ?? 'exploring',
    priorSupplements: existingModifiers?.priorSupplements ?? [],
    contraindications: [
      ...(existingModifiers?.contraindications ?? []),
      ...conditions.filter(c => {
        const cl = c.toLowerCase();
        return cl.includes('cancer') || cl.includes('thrombo') || cl.includes('dvt');
      }),
    ],
  };
}
